export class CalApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly details?: unknown,
  ) {
    super(message);
  }

  /**
   * Normalizes Cal API error payloads into proper Error instances.
   * Supports both nested shape { error: { code, message } } and top-level { message }.
   * If statusCode is 429 or code is ThrottlerException, returns CalApiRateLimitError.
   */
  static parse(error: unknown, statusCode?: number): CalApiError | unknown {
    const status = typeof statusCode === "number" ? statusCode : 400;

    // String payloads: treat as direct message
    if (typeof error === "string") {
      return status === 429
        ? new CalApiRateLimitError(error, error)
        : new CalApiError(error, status, error);
    }

    if (typeof error === "object" && error !== null) {
      // Nested Cal error object
      if ("error" in error) {
        const nested: unknown = (error as Record<string, unknown>).error;
        if (typeof nested === "object" && nested !== null) {
          const nestedObj = nested as Record<string, unknown>;
          const code = typeof nestedObj.code === "string" ? nestedObj.code : undefined;
          const message = typeof nestedObj.message === "string" ? nestedObj.message : undefined;
          if (code === "ThrottlerException" || status === 429) {
            return new CalApiRateLimitError(message ?? "Too many requests", error);
          }
          if (typeof message === "string") {
            return new CalApiError(message, status, error);
          }
        } else if (typeof nested === "string") {
          return status === 429
            ? new CalApiRateLimitError(nested, error)
            : new CalApiError(nested, status, error);
        }
      }

      // Top-level message-like fields
      const obj = error as Record<string, unknown>;
      const messageFields = ["message", "error_description", "detail"] as const;
      for (const field of messageFields) {
        const value = obj[field];
        if (typeof value === "string" && value.length > 0) {
          return status === 429
            ? new CalApiRateLimitError(value, error)
            : new CalApiError(value, status, error);
        }
      }
    }

    // Unknown shape; return original for caller to handle
    return error;
  }
}

export class CalApiRateLimitError extends CalApiError {
  constructor(message: string, details?: unknown) {
    super(message, 429, details);
  }

  static isInstance(error: unknown): error is CalApiRateLimitError {
    return error instanceof CalApiRateLimitError;
  }
}
